"use client";

import { useQueryClient } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect } from "react";
import { useApi } from "@/lib/fetch-client";
import { useAuth } from "@/lib/auth-client";
import { getLoginRedirectPath, isAuthPathname, isSessionInvalidError } from "@llmgateway/shared/auth-session";
import type { Route } from "next";
export interface UserUpdateData {
  name?: string;
  email?: string;
}
export interface PasswordUpdateData {
  currentPassword: string;
  newPassword: string;
}
export interface UseUserOptions {
  redirectTo?: string;
  redirectWhen?: "authenticated" | "unauthenticated";
  checkOnboarding?: boolean;
}
let sessionInvalidationInProgress = false;
let sessionInvalidationHandled = false;
export function useUser(options?: UseUserOptions) {
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();
  const api = useApi();
  const {
    signOut
  } = useAuth();
  const {
    data,
    isLoading,
    error
  } = api.useQuery("get", "/user/me", {}, {
    retry: 0,
    staleTime: 5 * 60 * 1000,
    // 5 minutes
    refetchOnWindowFocus: false
  });
  if (data) {
    posthog.identify(data.user.id, {
      email: data.user.email,
      name: data.user.name,
      onboarding_completed: data.user.onboardingCompleted
    });
  }
  useEffect(() => {
    if (data?.user) {
      sessionInvalidationHandled = false;
    }
  }, [data?.user]);
  useEffect(() => {
    if (!error || !isSessionInvalidError(error)) {
      return;
    }
    if (sessionInvalidationInProgress || sessionInvalidationHandled) {
      return;
    }
    sessionInvalidationInProgress = true;
    void signOut().catch(() => undefined).finally(() => {
      queryClient.clear();
      sessionInvalidationHandled = true;
      sessionInvalidationInProgress = false;
      if (options?.redirectWhen === "unauthenticated" && !isAuthPathname(pathname)) {
        router.replace(getLoginRedirectPath(pathname) as Route);
      }
    });
  }, [error, options?.redirectWhen, pathname, queryClient, router, signOut]);

  // Handle existing redirect logic
  useEffect(() => {
    if (!options?.redirectTo || !options?.redirectWhen) {
      return;
    }
    const {
      redirectTo,
      redirectWhen,
      checkOnboarding
    } = options;
    const hasUser = !!data?.user;
    if (error && isSessionInvalidError(error)) {
      return;
    }
    if (redirectWhen === "authenticated" && hasUser && !isLoading && !error) {
      if (checkOnboarding && !data.user.onboardingCompleted) {
        router.push("/onboarding");
      } else {
        router.push(redirectTo as Route);
      }
    } else if (redirectWhen === "unauthenticated" && !isLoading && (!hasUser || error)) {
      router.push(redirectTo as Route);
    }
  }, [data?.user, isLoading, error, router, options?.redirectTo, options?.redirectWhen, options?.checkOnboarding, options]);
  return {
    user: data?.user ?? null,
    isLoading,
    error,
    data
  };
}
export function useUpdateUser() {
  const queryClient = useQueryClient();
  const api = useApi();
  return api.useMutation("patch", "/user/me", {
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["user"]
      });
      void queryClient.invalidateQueries({
        queryKey: ["session"]
      });
    }
  });
}
export function useUpdatePassword() {
  const api = useApi();
  return api.useMutation("put", "/user/password");
}
export function useDeleteAccount() {
  const api = useApi();
  return api.useMutation("delete", "/user/me");
}